/**
 * Removes only auth/session-related keys from localStorage.
 *
 * Unlike localStorage.clear(), this preserves third-party SDK state
 * (LaunchDarkly, Segment, Highlight.io, etc.) so that pre-login
 * feature flag targeting and observability continue to work.
 *
 * ## Why not localStorage.clear()?
 *
 * The LOGOUT reducer, login form submit, and mobile logout all need to
 * clean up session state. A naive `localStorage.clear()` wipes
 * everything — including LaunchDarkly's anonymous device ID
 * (ld:$anonUserId) — making it impossible to target users with feature
 * flags before login.
 *
 * ## Adding new keys
 *
 * When adding a new localStorage key to the app, decide whether it
 * should be cleared on logout:
 * - **User-scoped or auth data** (tokens, selected user/facility,
 *   per-user preferences): add it to SESSION_KEYS or SESSION_KEY_PREFIXES below.
 * - **Third-party SDK state** (ld:*, ajs_*, sessionID, highlightIdentifier):
 *   do NOT add it — these must survive logout.
 * - **Device/browser preferences**:
 *   do NOT add it — these are not user-scoped.
 */
export function clearSessionStorage(): void {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && isSessionKey(key)) {
      localStorage.removeItem(key);
    }
  }
}

/** Exact localStorage keys to remove on logout. */
const SESSION_KEYS = new Set([
  "state", // Serialized Redux session state
  "selectedUser", // Currently selected user ID
  // "selectedFacility" is intentionally NOT listed here. LOGOUT is also
  // dispatched when a Cognito session refresh fails (initCognitoSession),
  // which triggers clearSessionStorage(). Clearing selectedFacility in
  // that path erases the facility the user was trying to switch to, so
  // after re-login they always land on the first facility instead of the
  // one they chose. The explicit logout() action creator removes it
  // directly (see src/modules/session.ts).
  "emailToVerify", // Email used during sign-in link flow
  // NOTE: "emailForSignIn" is intentionally NOT cleared here. It is the
  // opt-in "remember my email on this device" value (DEVOP-5493) and must
  // survive logout so the login page can pre-fill it next time — it behaves
  // like a device/browser preference, not user-scoped session state.
  "isSideMenuCollapsed", // Side menu UI state (user-scoped)
  "dateSelectionView", // Shift calendar view preference
  "disputeHoursInputPreference", // Invoice dispute input preference
]);

/** localStorage key prefixes to remove on logout (matches startsWith). */
const SESSION_KEY_PREFIXES = [
  "numberOfSession_", // Per-user session counter (numberOfSession_{userId})
  "CognitoIdentityServiceProvider", // AWS Cognito/Amplify auth tokens
  "claimWorkplaceFormData-", // Unverified placement claim form data per workplace
  "workerDocumentDownloadPollingRequest-", // Document download polling state per user
  "quizReviewStatus-", // Quiz completion status per user
  "pushNotificationToken_", // Push notification token per user
];

function isSessionKey(key: string): boolean {
  return SESSION_KEYS.has(key) || SESSION_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));
}
